import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MapPin, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { projectsAPI } from '../services/api';

export const Projects = () => {
  const { t, language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      if (response.success) {
        setProjects(response.projects);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('planung') || statusLower.includes('planning')) return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    if (statusLower.includes('verfügbar') || statusLower.includes('available')) return 'bg-green-500/20 text-green-300 border-green-500/30';
    if (statusLower.includes('verkauft') || statusLower.includes('sold')) return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
  };

  if (loading) {
    return (
      <section id="projects" className="py-20 bg-[#0a0a0a] relative">
        <div className="container mx-auto px-4">
          <div className="text-center text-gray-400">Lädt Projekte...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="projects" className="py-20 bg-[#0a0a0a] relative">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.projects.title}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t.projects.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {projects.map((project) => {
            const title = project.title[language] || project.title.de;
            const location = project.location[language] || project.location.de;
            const type = project.type[language] || project.type.de;
            const status = project.status[language] || project.status.de;
            
            return (
              <Card
                key={project.id}
                className="bg-[#1a1a1a] border-[#C4A55A]/20 overflow-hidden group hover:border-[#C4A55A]/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#C4A55A]/20"
              >
                {/* Project Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  
                  {/* ROI Badge */}
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-[#C4A55A] text-black border-none font-semibold">
                      ROI {project.roi}
                    </Badge>
                  </div>
                  
                  {/* Status Badge */}
                  <div className="absolute bottom-4 left-4">
                    <Badge className={`${getStatusColor(status)} border`}>
                      {status}
                    </Badge>
                  </div>
                </div>

                <CardHeader>
                  <CardTitle className="text-xl text-white group-hover:text-[#C4A55A] transition-colors duration-300">
                    {title}
                  </CardTitle>
                  <CardDescription className="flex items-center text-gray-400">
                    <MapPin className="h-4 w-4 mr-1" />
                    {location}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="mb-4">
                    <span className="text-sm text-gray-500">Typ: </span>
                    <span className="text-sm text-gray-300">{type}</span>
                  </div>
                  
                  <Button
                    variant="ghost"
                    className="w-full text-[#C4A55A] hover:bg-[#C4A55A]/10 hover:text-[#D4B56A] transition-all duration-300 group/btn"
                  >
                    {t.projects.viewDetails}
                    <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};